/**
 * @file     LeadFlowStep.tsx
 * @readme   ./CLAUDE.md
 * @purpose  Core option-selection component supporting single-select (auto-advance) and multi-select (confirm button) modes for all deal-finder question steps.
 */
'use client';

import { useEffect, useRef, useState } from 'react';
import { glassMaterial } from '@/design-system/tokens/glass';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { RayInfoTooltip } from '@/components/ui/RayInfoTooltip';
import { zLayers } from '@/design-system/tokens/zLayers';
import { FlowSurface } from '@/features/lead-flows/components/FlowSurface';
import { FlowHeading } from '@/features/lead-flows/components/FlowHeading';
import {
  flowColors,
  flowFocusRing,
  flowPrimaryButtonSx,
  flowRadius,
  flowTransition,
  reducedMotion,
} from '@/features/lead-flows/components/flowKitTokens';
import type { LeadFlowOption } from './types';

type BaseProps<TValue extends string> = {
  question: string;
  description?: string;
  options: LeadFlowOption<TValue>[];
  /**
   * `card` (default) draws the step's own card, as the deal-finder flow
   * needs. `bare` renders flat inside a card the caller already drew.
   */
  surface?: 'card' | 'bare';
  /** Level of the question heading. The page owns the h1. */
  headingComponent?: 'h2' | 'h3';
};

type SingleSelectProps<TValue extends string> = BaseProps<TValue> & {
  mode?: 'single';
  value?: TValue;
  onSelect: (value: TValue) => void;
};

type MultiSelectProps<TValue extends string> = BaseProps<TValue> & {
  mode: 'multi';
  value?: TValue[];
  onSelect: (values: TValue[]) => void;
};

export type LeadFlowStepProps<TValue extends string> =
  | SingleSelectProps<TValue>
  | MultiSelectProps<TValue>;

const NONE_VALUE = 'not-sure-yet';

export const LeadFlowStep = <TValue extends string>(props: LeadFlowStepProps<TValue>) => {
  const { question, description, options, surface = 'card', headingComponent = 'h2' } = props;
  const { t } = useTranslation();

  const [pendingValues, setPendingValues] = useState<TValue[]>(() => {
    if (props.mode === 'multi') {
      return props.value ?? [];
    }
    return [];
  });

  const isMulti = props.mode === 'multi';
  // Label-only options (no description line) are short answers; tall cards
  // stacked one per row made a 6-option question nearly a full phone screen.
  const compact = options.every((option) => !option.description);
  const canConfirm = isMulti && pendingValues.length > 0;

  // Stable portal target for the mobile pinned-bottom Continue. Lives in
  // app/layout.tsx as <div id="funnel-pinned-root">, OUTSIDE the funnel's
  // animating ancestors. We need to portal out because StepTransition wraps
  // the step in a translateY transform — and a transform ancestor becomes
  // the containing block for any position:fixed descendant, so a fixed bar
  // rendered in-tree gets pinned to the animating wrapper instead of the
  // viewport (i.e. it scrolls). The portal root never unmounts so this is
  // crash-safe, unlike portaling to document.body which we tried before.
  const [pinnedRoot, setPinnedRoot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPinnedRoot(document.getElementById('funnel-pinned-root'));
  }, []);

  // Guards against double-fire from rapid taps / iOS ghost clicks during the
  // step-transition animation. Single-select advances on first tap; the second
  // tap (intended for the same tile or a misclick on the next step) is dropped.
  const isAdvancingRef = useRef(false);
  useEffect(() => {
    isAdvancingRef.current = false;
  }, [props.mode, options]);

  const handleSingleSelect = (value: TValue) => {
    if (props.mode !== 'multi') {
      if (isAdvancingRef.current) return;
      isAdvancingRef.current = true;
      props.onSelect(value);
    }
  };

  const handleMultiToggle = (value: TValue) => {
    if (props.mode !== 'multi') return;

    setPendingValues((current) => {
      if (value === (NONE_VALUE as TValue)) {
        return current.includes(value) ? [] : [value];
      }

      const withoutNone = current.filter((v) => v !== (NONE_VALUE as TValue));

      if (withoutNone.includes(value)) {
        return withoutNone.filter((v) => v !== value);
      }

      return [...withoutNone, value];
    });
  };

  const handleMultiConfirm = () => {
    if (props.mode === 'multi' && pendingValues.length > 0 && !isAdvancingRef.current) {
      isAdvancingRef.current = true;
      props.onSelect(pendingValues);
    }
  };

  const isOptionSelected = (value: TValue): boolean => {
    if (isMulti) {
      return pendingValues.includes(value);
    }
    return props.value === value;
  };

  return (
    <FlowSurface standalone={surface === 'card'} sx={surface === 'bare' ? { p: 0, border: 0, backgroundColor: 'transparent' } : undefined}>
      <FlowHeading level={headingComponent} size={surface === 'card' ? 'lg' : 'md'} subtext={description}>
        {question}
        {/^I[ '"]/.test(question) && <RayInfoTooltip />}
      </FlowHeading>

      <Box
        role="group"
        aria-label={question}
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: compact ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
            lg: options.length === 2 ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
          },
          gap: 2,
        }}
      >
        {options.map((option) => {
          const selected = isOptionSelected(option.value);
          const isNotSure = option.value === NONE_VALUE;
          const selectedBorder = isNotSure ? flowColors.amberText : flowColors.interactivePrimary;

          return (
            <Box
              key={option.value}
              component="button"
              type="button"
              aria-pressed={selected}
              onClick={() =>
                isMulti
                  ? handleMultiToggle(option.value)
                  : handleSingleSelect(option.value)
              }
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                width: '100%',
                minHeight: compact ? 48 : 88,
                m: 0,
                px: 4,
                py: compact ? 2 : 4,
                gap: 0.5,
                border: `1px solid ${selected ? selectedBorder : flowColors.borderStrong}`,
                // A selected tile keeps a 1px border and adds an inset ring, so
                // the state reads in shape as well as colour without the grid
                // shifting.
                boxShadow: selected ? `inset 0 0 0 1px ${selectedBorder}` : 'none',
                borderRadius: `${compact ? flowRadius.sm : flowRadius.md}px`,
                backgroundColor: selected ? flowColors.chip : flowColors.surfaceCard,
                color: flowColors.textPrimary,
                font: 'inherit',
                textAlign: 'start',
                cursor: 'pointer',
                // Kills iOS double-tap-to-zoom and the 300ms tap delay,
                // and prevents the browser from issuing a synthetic click
                // after a touchend, which used to double-fire onClick.
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                transition: flowTransition('border-color', 'background-color', 'box-shadow'),
                '&:hover': { borderColor: selectedBorder },
                '&:active': { opacity: 0.92 },
                ...flowFocusRing,
                [reducedMotion]: { transition: 'none' },
              }}
            >
              <Typography component="span" sx={{ fontSize: 15.5, lineHeight: 1.35, fontWeight: selected ? 600 : 500, color: flowColors.textPrimary }}>
                {option.label}
              </Typography>
              {option.description ? (
                <Typography component="span" sx={{ fontSize: 13.5, lineHeight: 1.45, color: flowColors.textSecondary }}>
                  {option.description}
                </Typography>
              ) : null}
            </Box>
          );
        })}
      </Box>

      {/* Inline Continue, sm and up. On phones the option grid often pushes
          this below the fold, so the mobile flow uses a portaled
          pinned-bottom bar instead (see below). */}
      {isMulti && (
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
          <Button
            variant="contained"
            onClick={handleMultiConfirm}
            disabled={!canConfirm}
            sx={{ ...flowPrimaryButtonSx, minWidth: 160, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
          >
            {t('dealFinder.actions.continue', { defaultValue: 'Continue' })}
          </Button>
          {canConfirm && (
            <Typography sx={{ fontSize: 12, color: flowColors.textMuted, fontWeight: 500 }}>
              {t('dealFinder.actions.orSelectMore', { defaultValue: 'or select more' })}
            </Typography>
          )}
        </Box>
      )}

      {/* Mobile spacer: keeps the last option row from being visually
          crammed against the pinned Continue bar. Only when the bar is
          actually showing. */}
      {isMulti && canConfirm && (
        <Box sx={{ display: { xs: 'block', sm: 'none' }, height: 88 }} />
      )}

      {/* Mobile pinned-bottom Continue. Portaled into the stable
          #funnel-pinned-root in app/layout.tsx so that:
          (1) it stays viewport-fixed instead of being captured by
              StepTransition's transform ancestor,
          (2) it never unmounts during step transitions (the portal root
              outlives any individual step), avoiding the unmount crash from
              the earlier portal-to-body attempt. */}
      {isMulti && canConfirm && pinnedRoot && createPortal(
        <Box
          sx={{
            display: { xs: 'flex', sm: 'none' },
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: zLayers.appBar,
            justifyContent: 'center',
            pt: 1,
            pb: 'max(12px, env(safe-area-inset-bottom))',
            px: 5,
            ...glassMaterial('chrome', { edge: 'top' }),
            '@keyframes pinnedFadeIn': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
            animation: 'pinnedFadeIn 180ms ease-out',
            [reducedMotion]: { animation: 'none' },
          }}
        >
          <Button
            variant="contained"
            onClick={handleMultiConfirm}
            sx={{ ...flowPrimaryButtonSx, width: '100%', maxWidth: 480, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
          >
            {t('dealFinder.actions.continueWithCount', {
              defaultValue: 'Continue with {{count}} selected →',
              count: pendingValues.length,
            })}
          </Button>
        </Box>,
        pinnedRoot,
      )}
    </FlowSurface>
  );
};
